import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from '../../context/LanguageContext';
import founderImage from '../../assets/founder.webp';
import { aboutHeroImage, aboutSplitImage } from '../../assets/siteImages';
import { fadeUp, scrollViewport } from '../../motion/presets';
import '../../components/home-why/home-why.scss';
import '../../styles/page-hero-media.scss';
import './about.scss';

const About = () => {
  const { content } = useTranslation();
  const a = content.about;

  return (
    <main className="about-page">
      <section className="about-hero section-full">
        <div className="container page-hero-split">
          <div className="page-hero-split__copy">
            <motion.p
              className="about-hero__eyebrow"
              initial="hidden"
              whileInView="visible"
              viewport={scrollViewport}
              variants={fadeUp}
            >
              {a.heroEyebrow}
            </motion.p>
            <motion.h1
              className="about-hero__title"
              initial="hidden"
              whileInView="visible"
              viewport={scrollViewport}
              variants={fadeUp}
            >
              {a.heroTitle}
            </motion.h1>
            <motion.p
              className="about-hero__lead"
              initial="hidden"
              whileInView="visible"
              viewport={scrollViewport}
              variants={fadeUp}
            >
              {a.heroLead}
            </motion.p>
          </div>
          <motion.figure
            className="page-hero-split__media"
            initial="hidden"
            whileInView="visible"
            viewport={scrollViewport}
            variants={fadeUp}
          >
            <img
              className="page-hero-split__photo"
              src={aboutHeroImage}
              alt={a.heroPhotoAlt}
              loading="eager"
              decoding="async"
            />
          </motion.figure>
        </div>
      </section>

      <section className="about-block section-full">
        <div className="container about-block__inner">
          <motion.h2
            className="about-block__title"
            initial="hidden"
            whileInView="visible"
            viewport={scrollViewport}
            variants={fadeUp}
          >
            {a.standTitle}
          </motion.h2>
          <motion.p
            className="about-block__text"
            initial="hidden"
            whileInView="visible"
            viewport={scrollViewport}
            variants={fadeUp}
          >
            {a.standStart} <strong>{a.standEdu}</strong> {a.standEduNote}{' '}
            <strong>{a.standWork}</strong> {a.standWorkNote} <strong>{a.standProtection}</strong>{' '}
            {a.standEnd}
          </motion.p>
        </div>
      </section>

      <section className="parent-bg section-full" aria-labelledby="about-why-title">
        <div className="container home-why__shell">
          <motion.div
            className="home-why__card"
            initial="hidden"
            whileInView="visible"
            viewport={scrollViewport}
            variants={fadeUp}
          >
            <div className="home-why__card-inner">
              <div className="home-why__media">
                <img
                  className="home-why__photo"
                  src={aboutSplitImage}
                  alt={a.splitPhotoAlt}
                  loading="lazy"
                  decoding="async"
                />
              </div>

              <div className="home-why__copy">
                <div className="home-why__accent" aria-hidden="true" />
                <h2 id="about-why-title" className="home-why__title">
                  {a.splitTitle}
                </h2>
                <p className="home-why__lead">{a.splitLead}</p>

                <ul className="list-checks home-why__list">
                  {a.splitBullets.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>

                <div className="home-why__rule-wrap">
                  <hr className="divider-soft" />
                </div>
                <p className="home-why__closing">{a.splitClosing}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="about-founder section-full" aria-labelledby="about-founder-name">
        <div className="container">
          <motion.figure
            className="about-founder__card surface-muted"
            initial="hidden"
            whileInView="visible"
            viewport={scrollViewport}
            variants={fadeUp}
          >
            <p className="about-founder__label">{a.founderLabel}</p>
            <img
              className="about-founder__photo"
              src={founderImage}
              alt={a.founderName}
              loading="lazy"
              decoding="async"
            />
            <div className="about-founder__body">
              <h2 id="about-founder-name" className="about-founder__name">
                {a.founderName}
              </h2>
              <p className="about-founder__role">{a.founderRole}</p>
              <blockquote className="about-founder__quote">
                <p>{a.founderQuote}</p>
              </blockquote>
              <figcaption className="about-founder__cite">{a.founderCite}</figcaption>
              <p className="about-founder__bio">{a.founderBio}</p>
              <p className="about-founder__contact">
                <a href={`mailto:${content.common.mail}`}>{content.common.mail}</a>
                <span className="about-founder__sep"> · </span>
                <a href={`https://${content.common.domain}`} rel="noreferrer" target="_blank">
                  {content.common.domain}
                </a>
              </p>
              <p className="about-founder__closing">{a.founderClosing}</p>
            </div>
          </motion.figure>
        </div>
      </section>

      <section className="about-cta section-full">
        <div className="container about-cta__inner">
          <motion.h2
            className="about-cta__title"
            initial="hidden"
            whileInView="visible"
            viewport={scrollViewport}
            variants={fadeUp}
          >
            {a.ctaTitle}
          </motion.h2>
          <motion.p
            className="about-cta__text"
            initial="hidden"
            whileInView="visible"
            viewport={scrollViewport}
            variants={fadeUp}
          >
            {a.ctaText}
          </motion.p>
          <motion.div
            className="about-cta__actions"
            initial="hidden"
            whileInView="visible"
            viewport={scrollViewport}
            variants={fadeUp}
          >
            <Link className="btn btn-primary about-cta__btn" to="/">
              {a.ctaHome}
            </Link>
            <Link className="about-cta__link" to="/contact">
              {a.ctaContact}
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default About;
